import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  model,
  OnChanges,
  Output,
  Signal,
  SimpleChanges,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";

import { tap } from "rxjs/operators";

import { createDispatchMap, Store } from "@ngxs/store";

import { TableStateActions } from "../state/table.actions";
import { TableState } from "../state/table.state";

import { BarcodeScannerComponent } from "../../barcode-scanner/barcode-scanner.component";

import { effectWith } from "ngx-signal-operators";

@Component({
  selector: "lib-table-filter-qr",
  templateUrl: "./table-filter-qr.component.html",
  styleUrls: ["./table-filter-qr.component.css"],
  imports: [CommonModule, FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableFilterQrComponent implements OnChanges {
  /**
   * Component configuration inputs.
   */
  readonly tableName = input<string>();
  readonly showScanner = input<boolean>(true);

  /**
   * @deprecated Use the query from a query param instead.
   */
  readonly filterString = model("");

  /**
   * @todo Need to add the sorting param to the URL and update it when it changes.
   * This will allow the sorting to be preserved when the user refreshes the page or shares the URL.
   */
  readonly sortActive = input<string>();
  readonly query = input<string>();

  @Output() filterChange? = new EventEmitter<string>();
  @Output() enterKeyup? = new EventEmitter<string>();

  placeHolderText: Signal<string>;

  #actions = createDispatchMap({
    updateTableState: TableStateActions.UpdateState,
  });

  #dialog = inject(MatDialog);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);

  constructor(private store: Store) {
    this.placeHolderText = computed(() => {
      const tableName = this.tableName();
      return tableName ? `Search ${tableName.toLocaleLowerCase()}...` : "Search...";
    });

    // Set initial filter string from query param if it exists
    const queryParam = this.#activatedRoute.snapshot.queryParams["query"];
    if (queryParam) {
      this.filterString.set(queryParam);
    }

    effectWith(this.filterString)
      .filter((value) => value == null || value.length === 0 || value.length >= 2)
      .debounce(500)
      .run((value) => {
        const tableName = this.tableName();
        if (tableName) {
          this.#actions.updateTableState(tableName, { filterText: value?.toUpperCase() });
        }
        this.#router.navigate([], {
          queryParams: { query: value || null },
          queryParamsHandling: "merge",
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const tableName = this.tableName();
    if (tableName) {
      // A ?query= in the URL (e.g. a deep link from another page) wins over the table's last stored filter.
      const queryParam = this.#activatedRoute.snapshot.queryParams["query"];
      this.filterString.set(queryParam || (this.store.selectSnapshot(TableState.tableQuery(tableName))?.filterText ?? ""));
    }
  }

  openScannerDialog(): void {
    this.#dialog
      .open(BarcodeScannerComponent)
      .afterClosed()
      .pipe(
        takeUntilDestroyed(),
        tap((result: string) => {
          this.filterString.set(result);
        })
      )
      .subscribe();
  }
}
